import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Button,
  Flex,
  useToast,
  Spinner,
  Text,
  VStack,
  HStack,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  IconButton,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { FaArrowLeft, FaDownload, FaSync, FaExclamationTriangle } from 'react-icons/fa';
import { MdEmail, MdOpenInNew, MdTouchApp } from 'react-icons/md';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { getToken, API_BASE_URL } from '../../../../../services/api';
import RecipientTable from './components/RecipientTable';
import EngagementChart from './components/EngagementChart';
import TopLinks from './components/TopLinks';
import UndeliveredReportModal from './components/UndeliveredReportModal';

const CampaignDetail = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [campaign, setCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [linkAnalytics, setLinkAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [recipientFilter, setRecipientFilter] = useState('all');
  const [recipientPage, setRecipientPage] = useState(1);
  const [totalRecipients, setTotalRecipients] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showUndeliveredModal, setShowUndeliveredModal] = useState(false);

  useEffect(() => {
    fetchCampaignData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchCampaignData(true);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [campaignId]);

  useEffect(() => {
    fetchRecipients();
  }, [recipientFilter, recipientPage]);

  const fetchCampaignData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Fetch campaign analytics
      const [analyticsRes, linksRes] = await Promise.all([
        api.get(`/analytics/campaigns/${campaignId}`),
        api.get(`/analytics/campaigns/${campaignId}/links`)
      ]);

      if (analyticsRes.success) {
        setAnalytics(analyticsRes.analytics);
        setCampaign(analyticsRes.analytics.campaign);
      }

      if (linksRes.success) {
        setLinkAnalytics(linksRes.linkAnalytics);
      }

    } catch (error) {
      console.error('Error fetching campaign data:', error);
      toast({
        title: 'Erro ao carregar dados',
        description: error.response?.data?.message || 'Erro ao buscar dados da campanha',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const updateCampaignStats = async () => {
    setIsUpdating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/analytics/campaigns/${campaignId}/update-stats`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Estatísticas Atualizadas',
          description: `✅ ${data.details.trackingRecords} registros processados\n📧 Entregues: ${data.details.delivered} (${data.details.deliveryRate}%)\n👁️ Abertos: ${data.details.opened} (${data.details.openRate}%)\n🖱️ Clicados: ${data.details.clicked} (${data.details.clickRate}%)`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // Refresh the campaign data
        await fetchCampaignData();
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('Error updating stats:', error);
      toast({
        title: 'Erro ao atualizar',
        description: error.message || 'Não foi possível atualizar as estatísticas',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const fetchRecipients = async () => {
    try {
      const response = await api.get(`/analytics/campaigns/${campaignId}/recipients`, {
        params: {
          page: recipientPage,
          limit: 50,
          filter: recipientFilter,
          sortBy: 'email'
        }
      });

      if (response.success) {
        setRecipients(response.recipients);
        setTotalRecipients(response.pagination.total);
      }
    } catch (error) {
      console.error('Error fetching recipients:', error);
    }
  };

  const exportCampaign = async () => {
    try {
      // Use fetch directly for blob responses
      const response = await fetch(`${API_BASE_URL}/analytics/campaigns/${campaignId}/export`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export report');
      }

      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `campaign-${campaignId}-report.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: 'Relatório exportado',
        description: 'O arquivo CSV foi baixado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error exporting:', error);
      toast({
        title: 'Erro ao exportar',
        description: 'Não foi possível exportar o relatório',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  if (!campaign || !analytics) {
    return (
      <Box p={6}>
        <Text>Campanha não encontrada</Text>
        <Button mt={4} onClick={() => navigate('/admin/email/analytics')}>
          Voltar
        </Button>
      </Box>
    );
  }

  return (
    <Box p={6}>
      {/* Breadcrumb */}
      <Breadcrumb spacing="8px" separator={<ChevronRightIcon color="gray.500" />} mb={4}>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/admin/email">
            Email
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/admin/email/analytics">
            Analytics
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>{campaign.name}</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* Header */}
      <Flex justify="space-between" align="start" mb={6}>
        <VStack align="start" spacing={2}>
          <HStack spacing={4}>
            <Button
              onClick={() => navigate('/admin/email/analytics')}
              variant="outline"
              colorScheme="gray"
              size="sm"
              leftIcon={<span>←</span>}
            >
              Voltar para Analytics
            </Button>
          </HStack>
          <HStack>
            <IconButton
              icon={<FaArrowLeft />}
              variant="ghost"
              onClick={() => navigate('/admin/email/analytics')}
              aria-label="Voltar"
            />
            <Heading size="lg">{campaign.name}</Heading>
          </HStack>
          <Text color="gray.600">{campaign.subject}</Text>
          <HStack spacing={3}>
            <Badge colorScheme={campaign.status === 'completed' ? 'green' : 'blue'}>
              {campaign.status}
            </Badge>
            <Text fontSize="sm" color="gray.500">
              Template: {campaign.templateId}
            </Text>
            <Text fontSize="sm" color="gray.500">
              Enviado: {new Date(campaign.startedAt).toLocaleDateString('pt-BR')}
            </Text>
          </HStack>
        </VStack>
        
        <HStack spacing={3}>
          <IconButton
            icon={<FaSync />}
            onClick={() => fetchCampaignData()}
            isLoading={refreshing}
            colorScheme="blue"
            variant="outline"
            aria-label="Atualizar"
            title="Atualizar dados"
          />
          <Button
            leftIcon={<FaSync />}
            colorScheme="blue"
            size="sm"
            onClick={updateCampaignStats}
            isLoading={isUpdating}
            loadingText="Atualizando..."
            title="Atualizar estatísticas do SendGrid"
            mr={2}
          >
            Atualizar Stats
          </Button>
          <Button
            leftIcon={<FaExclamationTriangle />}
            colorScheme="orange"
            onClick={() => setShowUndeliveredModal(true)}
            mr={2}
          >
            Relatório Não Entregues
          </Button>
          <Button
            leftIcon={<FaDownload />}
            colorScheme="green"
            onClick={exportCampaign}
          >
            Exportar CSV
          </Button>
        </HStack>
      </Flex>

      {/* Metrics Overview */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
        <Box bg="white" p={4} borderRadius="lg" shadow="sm">
          <Stat>
            <StatLabel>Taxa de Entrega</StatLabel>
            <StatNumber>
              {analytics.metrics.totalDelivered}/{analytics.metrics.totalSent}
            </StatNumber>
            <Progress 
              value={(analytics.metrics.totalDelivered / analytics.metrics.totalSent) * 100} 
              colorScheme="green" 
              size="sm" 
              mt={2}
            />
            <StatHelpText>
              {((analytics.metrics.totalDelivered / analytics.metrics.totalSent) * 100).toFixed(1)}%
            </StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={4} borderRadius="lg" shadow="sm">
          <Stat>
            <StatLabel>Taxa de Abertura</StatLabel>
            <StatNumber>{analytics.metrics.openRate}%</StatNumber>
            <Progress 
              value={parseFloat(analytics.metrics.openRate)} 
              colorScheme="purple" 
              size="sm" 
              mt={2}
            />
            <StatHelpText>
              {analytics.metrics.totalOpened} aberturas únicas
            </StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={4} borderRadius="lg" shadow="sm">
          <Stat>
            <StatLabel>Taxa de Cliques</StatLabel>
            <StatNumber>{analytics.metrics.clickRate}%</StatNumber>
            <Progress 
              value={parseFloat(analytics.metrics.clickRate)} 
              colorScheme="orange" 
              size="sm" 
              mt={2}
            />
            <StatHelpText>
              {analytics.metrics.totalClicked} cliques únicos
            </StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={4} borderRadius="lg" shadow="sm">
          <Stat>
            <StatLabel>Taxa de Rejeição</StatLabel>
            <StatNumber>{analytics.metrics.bounceRate}%</StatNumber>
            <Progress 
              value={parseFloat(analytics.metrics.bounceRate)} 
              colorScheme="red" 
              size="sm" 
              mt={2}
            />
            <StatHelpText>
              {analytics.metrics.totalBounced} emails rejeitados
            </StatHelpText>
          </Stat>
        </Box>
      </SimpleGrid>

      {/* Tabs */}
      <Tabs colorScheme="blue" variant="enclosed">
        <TabList>
          <Tab>Engajamento</Tab>
          <Tab>Destinatários</Tab>
          <Tab>Links Clicados</Tab>
          <Tab>Por Tipo de Destinatário</Tab>
        </TabList>

        <TabPanels>
          {/* Engagement Chart */}
          <TabPanel>
            <EngagementChart 
              data={linkAnalytics?.clickTimeline || []} 
              campaignId={campaignId}
            />
          </TabPanel>

          {/* Recipients Table */}
          <TabPanel>
            <RecipientTable
              recipients={recipients}
              totalRecipients={totalRecipients}
              currentPage={recipientPage}
              onPageChange={setRecipientPage}
              filter={recipientFilter}
              onFilterChange={setRecipientFilter}
            />
          </TabPanel>

          {/* Top Links */}
          <TabPanel>
            <TopLinks links={linkAnalytics?.topLinks || []} />
          </TabPanel>

          {/* Engagement by Type */}
          <TabPanel>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              {analytics.engagementByType.map((type) => (
                <Box key={type.type} bg="white" p={6} borderRadius="lg" shadow="sm">
                  <VStack align="start" spacing={4}>
                    <Heading size="md" textTransform="uppercase">
                      {type.type}
                    </Heading>
                    
                    <Box w="full">
                      <Flex justify="space-between" mb={1}>
                        <Text fontSize="sm">Taxa de Abertura</Text>
                        <Text fontSize="sm" fontWeight="bold">
                          {type.openRate.toFixed(1)}%
                        </Text>
                      </Flex>
                      <Progress value={type.openRate} colorScheme="purple" size="sm" />
                    </Box>
                    
                    <Box w="full">
                      <Flex justify="space-between" mb={1}>
                        <Text fontSize="sm">Taxa de Cliques</Text>
                        <Text fontSize="sm" fontWeight="bold">
                          {type.clickRate.toFixed(1)}%
                        </Text>
                      </Flex>
                      <Progress value={type.clickRate} colorScheme="orange" size="sm" />
                    </Box>
                    
                    <VStack align="start" spacing={1} w="full" pt={2} borderTop="1px" borderColor="gray.100">
                      <Text fontSize="xs" color="gray.600">
                        Total: {type.total} destinatários
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Abriram: {type.opened} | Clicaram: {type.clicked}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Média de aberturas: {type.avgOpens}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Média de cliques: {type.avgClicks}
                      </Text>
                    </VStack>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </TabPanel>
        </TabPanels>
      </Tabs>
      {/* Undelivered Report Modal */}
      <UndeliveredReportModal
        isOpen={showUndeliveredModal}
        onClose={() => setShowUndeliveredModal(false)}
        campaignId={campaignId}
        campaignName={campaign?.name}
      />
    </Box>
  );
};

export default CampaignDetail;